import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Buildings, GraduationCap, UsersThree, CalendarBlank } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = [
  { key: "campus", label: "Campus", icon: Buildings },
  { key: "filiere", label: "Filières", icon: GraduationCap },
  { key: "promotion", label: "Promotions", icon: UsersThree },
  { key: "semestre", label: "Semestres", icon: CalendarBlank },
];

// Pour chaque type d'élément, le type de son parent dans la hiérarchie
// (une promotion appartient à une filière, un semestre à une promotion).
const PARENT_TYPE = { promotion: "filiere", semestre: "promotion" };

export default function Academic() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("filiere");
  const [parent, setParent] = useState("");

  const load = () => api.get("/academic").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const parentType = PARENT_TYPE[type];
  const parentOptions = useMemo(
    () => (parentType ? items.filter((i) => i.type === parentType) : []),
    [items, parentType]
  );

  // Si on change de type et que l'ancien parent choisi ne correspond plus, on réinitialise.
  useEffect(() => { setParent(""); }, [type]);

  const childCount = (id) => items.filter((i) => i.parent === id).length;

  const add = async () => {
    if (!name.trim()) return;
    if (parentType && !parent) {
      toast.error(`Choisissez d'abord ${parentType === "filiere" ? "une filière" : "une promotion"} parente.`);
      return;
    }
    try {
      await api.post("/academic", { name, type, parent: parentType ? parent : null });
      toast.success("Ajouté");
      setName("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (item) => {
    const n = childCount(item.id);
    if (n > 0) {
      toast.error(`Impossible de supprimer : ${n} élément(s) encore rattaché(s) (promotions ou semestres). Supprimez-les d'abord.`);
      return;
    }
    try { await api.delete(`/academic/${item.id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filieres = items.filter((i) => i.type === "filiere");
  const campuses = items.filter((i) => i.type === "campus");
  const promotionsOf = (filiereId) => items.filter((i) => i.type === "promotion" && i.parent === filiereId);
  const semestresOf = (promotionId) => items.filter((i) => i.type === "semestre" && i.parent === promotionId);

  return (
    <div className="space-y-6" data-testid="academic-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Structure</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Gestion académique</h1>
        <p className="text-sm text-muted-foreground">Une promotion est rattachée à une filière, un semestre à une promotion. Cette hiérarchie alimente ensuite les listes déroulantes (comptes étudiants, groupes...).</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-5">
        <div className="flex-1 min-w-48"><Label>Nom</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Finance" data-testid="academic-name" /></div>
        <div className="w-48">
          <Label>Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="academic-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {parentType && (
          <div className="w-56">
            <Label>{parentType === "filiere" ? "Filière parente" : "Promotion parente"}</Label>
            <Select value={parent} onValueChange={setParent}>
              <SelectTrigger data-testid="academic-parent">
                <SelectValue placeholder={parentOptions.length === 0 ? "Aucune disponible" : "Choisir..."} />
              </SelectTrigger>
              <SelectContent>
                {parentOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button onClick={add} className="bg-primary hover:bg-primary/90" data-testid="add-academic-btn"><Plus size={18} className="mr-2" /> Ajouter</Button>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Buildings size={20} weight="duotone" /></div>
            <h3 className="font-display font-bold">Campus</h3>
          </div>
          <div className="space-y-2">
            {campuses.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2 text-sm">
                {c.name}
                <button onClick={() => del(c)}><Trash size={14} className="text-accent" /></button>
              </div>
            ))}
            {campuses.length === 0 && <p className="text-xs text-muted-foreground">Aucun élément</p>}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><GraduationCap size={20} weight="duotone" /></div>
            <h3 className="font-display font-bold">Filières → Promotions → Semestres</h3>
          </div>
          <div className="space-y-4">
            {filieres.map((f) => (
              <div key={f.id} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{f.name}</span>
                  <button onClick={() => del(f)}><Trash size={14} className="text-accent" /></button>
                </div>
                <div className="mt-2 ml-3 space-y-2 border-l-2 border-border pl-3">
                  {promotionsOf(f.id).map((p) => (
                    <div key={p.id}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 font-medium"><UsersThree size={14} className="text-muted-foreground" /> {p.name}</span>
                        <button onClick={() => del(p)}><Trash size={13} className="text-accent" /></button>
                      </div>
                      <div className="mt-1 ml-3 flex flex-wrap gap-1.5 border-l-2 border-border pl-3">
                        {semestresOf(p.id).map((s) => (
                          <span key={s.id} className="flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs">
                            <CalendarBlank size={12} /> {s.name}
                            <button onClick={() => del(s)}><Trash size={11} className="text-accent" /></button>
                          </span>
                        ))}
                        {semestresOf(p.id).length === 0 && <span className="text-xs text-muted-foreground">Aucun semestre</span>}
                      </div>
                    </div>
                  ))}
                  {promotionsOf(f.id).length === 0 && <p className="text-xs text-muted-foreground">Aucune promotion rattachée</p>}
                </div>
              </div>
            ))}
            {filieres.length === 0 && <p className="text-xs text-muted-foreground">Aucune filière. Ajoutez-en une pour commencer.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
